import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // account locking state management
  const [accountLocked, setAccountLocked] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedToken = localStorage.getItem('authToken');
        const savedUser = localStorage.getItem('authUser');

        if (savedToken && savedUser) {
          console.log('🔄 Restoring auth from localStorage');
          console.log(
            '- Saved token:',
            savedToken ? savedToken.substring(0, 20) + '...' : 'none'
          );
          console.log('- Saved user:', JSON.parse(savedUser));

          setToken(savedToken);
          setUser(JSON.parse(savedUser));
          setIsAuthenticated(true);

          console.log('✅ Auth restored successfully');
        } else {
          console.log('❌ No saved auth found in localStorage');
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback((userData, authToken) => {
    console.log('🔑 Login called with:', {
      userData: userData?.email,
      role: userData?.role,
      token: authToken ? 'present' : 'missing',
      fullUserData: userData,
    });

    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);

    // Reset account lock states on successful login
    setAccountLocked(false);
    setFailedAttempts(0);

    localStorage.setItem('authToken', authToken);
    localStorage.setItem('authUser', JSON.stringify(userData));

    console.log('✅ Login complete - Token and user saved to localStorage');
    console.log('- Saved user:', userData);
    console.log('- User role:', userData?.role);
    console.log(
      '- Saved token:',
      authToken ? authToken.substring(0, 20) + '...' : 'none'
    );
  }, []);

  const logout = useCallback(() => {
    console.log('🚪 Logging out (clearing frontend state)');

    setUser(null);
    setToken(null);
    setIsAuthenticated(false);

    // Reset account lock states on logout
    setAccountLocked(false);
    setFailedAttempts(0);

    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');

    console.log('✅ Frontend auth state cleared');
  }, []);

  // Stable + safe merge (uses latest state via functional update)
  const updateUser = useCallback((userData) => {
    console.log('🔄 Updating user context');
    console.log('- New user data:', userData);

    setUser((prevUser) => {
      const updatedUser = {
        ...prevUser,
        ...userData,
        // Ensure critical fields are preserved
        userId: prevUser?.userId || userData?.userId,
        email: userData?.email || prevUser?.email,
        phone: userData?.phone || prevUser?.phone,
        role: userData?.role || prevUser?.role,
      };

      console.log('- Final merged user:', updatedUser);
      localStorage.setItem('authUser', JSON.stringify(updatedUser));
      console.log('✅ User context updated successfully');

      return updatedUser;
    });
  }, []);

  // handle login failures
  const handleLoginFailure = useCallback((errorData) => {
    console.log('⚠️ Handling login failure:', errorData);

    if (errorData?.accountLocked) {
      setAccountLocked(true);
      setFailedAttempts(3);
      console.log('🔒 Account locked');
    } else if (errorData?.remainingAttempts !== undefined) {
      const attempts = 3 - errorData.remainingAttempts;
      setFailedAttempts(attempts);
      console.log(`❌ Failed attempt ${attempts}/3`);
    }
  }, []);

  // to handle account unlock
  const handleAccountUnlock = useCallback(() => {
    console.log('🔓 Account unlocked');
    setAccountLocked(false);
    setFailedAttempts(0);
  }, []);

  // Role helper methods
  const isCandidate = user?.role === 'CANDIDATE';
  const isEmployer = user?.role === 'EMPLOYER';
  const isAgency = user?.role === 'AGENCY';
  const isAdmin = user?.role === 'ADMIN';
  const userRole = user?.role || null;

  // Memoize context value to avoid re-rendering consumers unnecessarily
  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated,
      accountLocked,
      failedAttempts,
      login,
      logout,
      updateUser,
      handleLoginFailure,
      handleAccountUnlock,

      // Role detection helpers
      isCandidate,
      isEmployer,
      isAgency,
      isAdmin,
      userRole,
    }),
    [
      user,
      token,
      loading,
      isAuthenticated,
      accountLocked,
      failedAttempts,
      login,
      logout,
      updateUser,
      handleLoginFailure,
      handleAccountUnlock,
      isCandidate,
      isEmployer,
      isAgency,
      isAdmin,
      userRole,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
